import { encounterApi } from '../encounterApi';
import { EncounterType } from '../types/encounterTypes';
import { EncounterCreateRequest } from '../requests/encounterCreateRequest';
import { EncounterUpdateRequest } from '../requests/encounterUpdateRequest';
import { EncounterCancelRequest } from '../requests/encounterCancelRequest';
import { EncounterGetByPatientRequest } from '../requests/encounterGetByPatientRequest';
import { EncounterLookupRequest } from '../requests/encounterLookupRequest';

const encounterEndpointsInjected = encounterApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get encounter by ID
    // GET /encounter?id=<id>
    getEncounter: builder.query<EncounterType, { id: string }>({
      query: ({ id }) => `/encounter?id=${encodeURIComponent(id)}`,
      providesTags: (result, error, { id }) => [{ type: 'Encounter', id }],
    }),

    // Get encounters by patient
    // GET /encountersForPatient?company=<company>&center=<center>&mrn=<mrn>
    getEncountersByPatient: builder.query<EncounterType[], EncounterGetByPatientRequest>({
      query: ({ company, center, mrn }) =>
        `/encountersForPatient?company=${encodeURIComponent(company)}&center=${encodeURIComponent(center)}&mrn=${encodeURIComponent(mrn)}`,
      providesTags: (result, error, { company, center, mrn }) => [
        { type: 'Encounters', id: `${company}-${center}-${mrn}` },
        'Encounters',
      ],
    }),

    // Create encounter
    // POST /encounter
    createEncounter: builder.mutation<EncounterType, EncounterCreateRequest>({
      query: (encounterCreateRequest) => ({
        url: '/encounter',
        method: 'POST',
        body: encounterCreateRequest,
      }),
      invalidatesTags: (result, error, request) => [
        'Encounters',
        // Invalidate the specific patient's encounters if we have the patient info
        ...(request.encounterView?.patient?.mrn &&
        request.encounterView?.caseIdentification?.company &&
        request.encounterView?.caseIdentification?.centerId
          ? [
              {
                type: 'Encounters' as const,
                id: `${request.encounterView.caseIdentification.company}-${request.encounterView.caseIdentification.centerId}-${request.encounterView.patient.mrn}`,
              },
            ]
          : []),
      ],
    }),

    // Update encounter
    // POST /encounterUpdate
    updateEncounter: builder.mutation<EncounterType, EncounterUpdateRequest>({
      query: (encounterUpdateRequest) => ({
        url: '/encounterUpdate',
        method: 'POST',
        body: encounterUpdateRequest,
      }),
      invalidatesTags: (result, error, request) => [
        'Encounters',
        'Encounter',
        // Invalidate the specific patient's encounters if we have the patient info
        ...(request.encounterView?.patient?.mrn &&
        request.encounterView?.caseIdentification?.company &&
        request.encounterView?.caseIdentification?.centerId
          ? [
              {
                type: 'Encounters' as const,
                id: `${request.encounterView.caseIdentification.company}-${request.encounterView.caseIdentification.centerId}-${request.encounterView.patient.mrn}`,
              },
            ]
          : []),
      ],
    }),

    // Cancel encounter
    // POST /encounterCancel
    cancelEncounter: builder.mutation<EncounterType, EncounterCancelRequest>({
      query: (encounterCancelRequest) => ({
        url: '/encounterCancel',
        method: 'POST',
        body: encounterCancelRequest,
      }),
      invalidatesTags: (result, error, request) => [
        'Encounters',
        'Encounter',
        { type: 'Encounter', id: request.encounterId },
      ],
    }),

    // Lookup encounters by search term
    // GET /encounterLookup?company=<company>&center=<center>&searchTerm=<searchTerm>
    lookupEncounters: builder.query<EncounterType[], EncounterLookupRequest>({
      query: ({ company, center, searchTerm }) =>
        `/encounterLookup?company=${encodeURIComponent(company)}&center=${encodeURIComponent(center)}&searchTerm=${encodeURIComponent(searchTerm)}`,
      providesTags: ['Encounters'],
    }),
  }),
  overrideExisting: false,
});

// Export enhanced API for slice extraReducers
export const encounterEndpoints = encounterEndpointsInjected;

export const {
  useGetEncounterQuery,
  useGetEncountersByPatientQuery,
  useCreateEncounterMutation,
  useUpdateEncounterMutation,
  useCancelEncounterMutation,
  useLookupEncountersQuery,
} = encounterEndpointsInjected;
